import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { dueLabel, taskWeight, type Task, type TaskImportance } from '@map/shared';
import { addTaskNote, fetchTaskNotes, fetchUniverse, setTaskStatus, updateTask } from './api.js';
import { IMPORTANCE, URGENCY } from './CaptureForm.js';
import { Markdown } from './MarkdownField.js';
import { MarkdownEditor } from './MarkdownEditor.js';
import { skyDate } from '../globe/TaskField.js';

/**
 * A rock, opened.
 *
 * Everything the capture form asked is editable here, and under it the task's
 * timeline: as many notes as the work takes, oldest first, each one keeping
 * the hour it was written. A note is never edited — the account of how
 * something went is worth more than a tidy one.
 */
export function TaskPanel({
  task,
  onClose,
  mobile = false,
}: {
  task: Task;
  onClose: () => void;
  mobile?: boolean;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(task.title);
  const [body, setBody] = useState(task.bodyMd ?? '');
  const [note, setNote] = useState('');
  const [editingBody, setEditingBody] = useState(false);

  // A different rock was clicked: the panel is the same component, so its
  // fields have to follow rather than keep the last task's text.
  useEffect(() => {
    setTitle(task.title);
    setBody(task.bodyMd ?? '');
    setNote('');
    setEditingBody(false);
  }, [task.id, task.title, task.bodyMd]);

  const universe = useQuery({ queryKey: ['universe'], queryFn: fetchUniverse });
  const planets = (universe.data?.planets ?? []).filter((p) => !p.archived);

  const notes = useQuery({
    queryKey: ['task-notes', task.id],
    queryFn: () => fetchTaskNotes(task.id),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['floating-tasks'] });

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof updateTask>[1]) => updateTask(task.id, patch),
    onSuccess: refresh,
  });

  const complete = useMutation({
    mutationFn: () => setTaskStatus(task.id, task.status === 'done' ? 'open' : 'done'),
    onSuccess: async () => {
      await refresh();
      if (task.status !== 'done') onClose();
    },
  });

  const write = useMutation({
    mutationFn: () => addTaskNote(task.id, note.trim()),
    onSuccess: async () => {
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['task-notes', task.id] });
    },
  });

  const patch = (fields: Record<string, unknown>) => save.mutate({ ...fields, version: task.version });
  const weight = taskWeight(task);
  const done = task.status === 'done';

  const segment = (active: boolean) =>
    `flex-1 rounded-[var(--radius-sm)] px-2 py-1.5 text-xs transition-colors ${
      active ? 'text-[var(--space-deep)]' : 'text-text-muted hover:text-text'
    }`;

  return (
    <div
      className="flex h-full flex-col"
      style={{ background: 'var(--surface-1)', borderLeft: mobile ? undefined : '1px solid var(--border)' }}
    >
      <header className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== task.title && patch({ title: title.trim() })}
            aria-label="Title"
            className="w-full bg-transparent text-base outline-none"
          />
          <p className="mt-0.5 text-xs" style={{ color: 'var(--debris-date)' }}>
            {done ? `Done ${skyDate(task.completedAt ?? task.createdAt)}` : dueLabel(task)}
            {!done && weight.overdue ? ' · overdue' : ''}
          </p>
        </div>
        {!mobile && (
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-[var(--radius-sm)] px-2 py-1 text-sm text-text-muted hover:text-text"
          >
            ✕
          </button>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <div className="grid gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-text-muted">Closest planet</span>
            <div className="flex flex-wrap gap-1.5">
              {planets.map((planet) => (
                <button
                  key={planet.id}
                  onClick={() => patch({ planetId: planet.id })}
                  className={`rounded-[var(--radius-md)] px-2.5 py-1.5 text-xs ${
                    task.planetId === planet.id ? 'text-[var(--space-deep)]' : 'text-text-muted hover:text-text'
                  }`}
                  style={{ background: task.planetId === planet.id ? 'var(--accent)' : 'var(--surface-0)' }}
                >
                  {planet.name}
                </button>
              ))}
            </div>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-text-muted">Importance</span>
            <div className="flex gap-1 rounded-[var(--radius-md)] p-1" style={{ background: 'var(--surface-0)' }}>
              {IMPORTANCE.map((option) => (
                <button
                  key={option.value}
                  title={option.hint}
                  onClick={() => patch({ importance: option.value as TaskImportance })}
                  className={segment(task.importance === option.value)}
                  style={task.importance === option.value ? { background: 'var(--accent)' } : undefined}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-text-muted">Urgency</span>
            <div className="flex gap-1 rounded-[var(--radius-md)] p-1" style={{ background: 'var(--surface-0)' }}>
              {URGENCY.map((option) => (
                <button
                  key={option.value}
                  title={option.hint}
                  onClick={() => patch({ urgency: option.value })}
                  className={segment(task.urgency === option.value)}
                  style={task.urgency === option.value ? { background: 'var(--accent)' } : undefined}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-text-muted">Due</span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={task.targetDate ?? ''}
                onChange={(e) => patch({ targetDate: e.target.value || null })}
                aria-label="Due date"
                className="min-w-0 flex-1 rounded-[var(--radius-md)] px-3 py-2 text-sm outline-none"
                style={{ background: 'var(--surface-0)' }}
              />
              {task.targetDate && (
                <button
                  onClick={() => patch({ targetDate: null })}
                  className="shrink-0 rounded-[var(--radius-sm)] px-2 py-1 text-xs text-text-muted hover:text-text"
                >
                  Clear
                </button>
              )}
            </div>
          </label>
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-xs text-text-muted">Detail</span>
            <button
              onClick={() => {
                if (editingBody) patch({ bodyMd: body.trim() ? body.trim() : null });
                setEditingBody((on) => !on);
              }}
              className="ml-auto rounded-[var(--radius-sm)] px-2 py-1 text-xs text-text-muted hover:text-text"
            >
              {editingBody ? 'Save' : 'Edit'}
            </button>
          </div>
          {editingBody ? (
            <MarkdownEditor value={body} onChange={setBody} minHeight="9rem" placeholder="Markdown." />
          ) : task.bodyMd ? (
            <Markdown source={task.bodyMd} />
          ) : (
            <p className="text-sm text-text-muted">Nothing written.</p>
          )}
        </div>

        <div className="mt-5">
          <span className="text-xs text-text-muted">Timeline</span>
          <div className="mt-2 space-y-2">
            {(notes.data?.notes ?? []).map((entry) => (
              <div
                key={entry.id}
                className="rounded-[var(--radius-md)] px-3 py-2"
                style={{ background: 'var(--surface-0)' }}
              >
                <div className="mb-1 text-[0.68rem] text-text-muted">
                  {skyDate(entry.createdAt)} ·{' '}
                  {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <Markdown source={entry.bodyMd} />
              </div>
            ))}
            {notes.data?.notes.length === 0 && (
              <p className="text-sm text-text-muted">Nothing noted yet.</p>
            )}
          </div>

          <div className="mt-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && note.trim()) write.mutate();
              }}
              rows={2}
              placeholder="Add to the timeline…"
              aria-label="New note"
              className="w-full resize-y rounded-[var(--radius-md)] px-3 py-2 text-sm outline-none"
              style={{ background: 'var(--surface-0)' }}
            />
            <div className="mt-1 flex items-center gap-2">
              <span className="text-[0.68rem] text-text-muted">Notes are kept, never edited.</span>
              <button
                onClick={() => note.trim() && write.mutate()}
                disabled={!note.trim() || write.isPending}
                className="ml-auto rounded-[var(--radius-sm)] px-2.5 py-1 text-xs disabled:opacity-40"
                style={{ background: 'var(--surface-2)' }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border px-4 py-3">
        <button
          onClick={() => complete.mutate()}
          disabled={complete.isPending}
          className="w-full rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium disabled:opacity-40"
          style={
            done
              ? { background: 'var(--surface-2)' }
              : { background: 'var(--accent)', color: 'var(--space-deep)' }
          }
        >
          {done ? 'Bring it back' : 'Complete'}
        </button>
      </footer>
    </div>
  );
}
